import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useEffect, useMemo, useState } from 'react';
import { Trash2, Download } from 'lucide-react';
import {
  UniversalLoadingState,
  Card,
  Button,
  Input,
  PeriodFilter,
  SearchField,
  StatusBadge,
  TABLE_PRESETS,
  NotificationCard,
  useToast, UniversalPage } from '@sighthost/ui';
import {
  adminFinancialsService,
  type ExpenseRow,
  type ExpenseCategory,
  EXPENSE_CATEGORY_LABEL,
} from '@/domains/financials/services/adminFinancialsService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { exportRowsAsCsv, todayStamp } from '@/shared/utils/csvExport';
import { formatZAR } from './components';
import { ConfirmDialog } from '@/shared/components/feedback/ConfirmDialog';

function ExpensesLedgerPageBody() {
  const { user } = useAuth();
  const role = (user?.app_metadata as { role?: string } | undefined)?.role;
  const isSuperAdmin = role === 'super_admin';

  const { toast } = useToast();
  const [rows, setRows] = useState<ExpenseRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | 'all'>('all');
  const [deleting, setDeleting] = useState<ExpenseRow | null>(null);

  function reload() {
    adminFinancialsService.listExpenses().then(setRows).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
  }

  useEffect(reload, []);

  // F-27: the confirm moved into a real dialog. Deleting an expense silently
  // restates every income statement that included it, which is more than a
  // browser prompt conveys.
  async function remove(id: string) {
    try {
      await adminFinancialsService.deleteExpense(id);
      toast({ tone: 'success', title: 'Expense deleted' });
      setDeleting(null);
      reload();
    } catch (e) {
      toast({ tone: 'danger', title: 'Delete failed', body: e instanceof Error ? e.message : String(e) });
    }
  }

  /**
   * Expenses are FLOW — `Incurred` is the event date, and it is a DATE column,
   * so the SAST day keys compare directly. AR-5 option (a): 2 rows against a
   * 200 cap (measured 2026-09-13); the set is complete several thousand times
   * over.
   */
  const table = useTableFilters<ExpenseRow>({
    rows: rows ?? undefined,
    dateMode: 'client',
    dateLabel: 'Incurred',
    dateOf: (r) => r.incurred_on,
    searchText: (r) => [r.description, r.notes ?? '', r.incurred_on].join(' '),
    facets: useMemo(
      () => [(r: ExpenseRow) => categoryFilter === 'all' || r.category === categoryFilter],
      [categoryFilter],
    ),
  });

  // `null` while loading is load-bearing below — the page early-returns a
  // spinner on it, so it must stay null rather than becoming an empty array.
  const filtered = rows === null ? null : table.rows;

  if (error) return <NotificationCard tone="danger" title="Could not load expenses" body={error} />;
  if (!filtered) return <UniversalLoadingState variant="spinner" caption="Loading expenses…" />;

  const total = filtered.reduce((s, r) => s + Number(r.amount), 0);

  return (
    <div className="space-y-4">
      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as ExpenseCategory | 'all')}
            className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <option value="all">All categories</option>
            {(Object.keys(EXPENSE_CATEGORY_LABEL) as ExpenseCategory[]).map((c) => (
              <option key={c} value={c}>{EXPENSE_CATEGORY_LABEL[c]}{c === 'payfast_fees' ? ' (auto)' : ''}</option>
            ))}
          </select>
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Incurred"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search description, notes"
            className="ml-auto w-full max-w-xs"
          />
          <span className="text-xs text-muted-foreground">
            {table.caption} · total {formatZAR(total)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const header = ['Date', 'Category', 'Description', 'Amount (ZAR)', 'Notes'];
              const dataRows = filtered.map((r) => [
                r.incurred_on,
                EXPENSE_CATEGORY_LABEL[r.category],
                r.description,
                Number(r.amount),
                r.notes ?? '',
              ]);
              exportRowsAsCsv(`expenses-${todayStamp()}.csv`, header, dataRows);
            }}
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </Card>

      <Card interactive={false}>
        {filtered.length === 0 ? (
          <p className="p-12 text-center text-sm text-muted-foreground">
            No expenses match. Add one from the Monthly statement tab.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <StatusBadge tone="info">{EXPENSE_CATEGORY_LABEL[r.category]}</StatusBadge>
                  <span className="text-foreground">{r.description}</span>
                  {r.notes && <span className="text-xs text-muted-foreground">· {r.notes}</span>}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{r.incurred_on}</span>
                  <span className="text-base font-semibold text-danger tabular-nums">{formatZAR(Number(r.amount))}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleting(r)}
                    disabled={!isSuperAdmin}
                    title={!isSuperAdmin ? 'super_admin only' : 'Delete'}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete this expense?"
        description="It is removed from every income statement, the 12-month report and the projections — not just this month. There is no undo."
        confirmLabel="Delete expense"
        tone="danger"
        onConfirm={() => {
          if (deleting) void remove(deleting.id);
        }}
      >
        {deleting && (
          <p className="mt-3 rounded border border-border bg-card-subtle p-3 text-sm">
            <span className="text-foreground">{deleting.description}</span>
            <span className="ml-2 font-semibold tabular-nums text-danger-dark">
              {formatZAR(Number(deleting.amount))}
            </span>
            <span className="ml-2 text-muted-foreground">· {deleting.incurred_on}</span>
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}

/**
 * These five pages previously rendered NO page header at all — no title, no
 * breadcrumbs — because FinancialReportsLayout supplied a nav row and nothing
 * else. That was a §4.4 violation hiding inside a §4.1 one, and it is why no
 * financial report had a back link.
 */
export default function ExpensesLedgerPage() {
  return (
    <UniversalPage
      title="Expenses ledger"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Financials', href: '/admin/financials' },
        { label: 'Expenses ledger' },
      ]}
    >
      <ExpensesLedgerPageBody />
    </UniversalPage>
  );
}
